import { useQuery, useMutation } from '@tanstack/vue-query';
import { useLocalStorage } from '@vueuse/core';
import { request } from '@/api/request';
import { cookieSignup, cookieConfirmSignup, cookieResendSignupCode } from '../cookieAuthApi';
import {
  whenNonNull,
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import type { User } from '@@/packages/shared/types/models/user';
import type { Organisation } from '@@/packages/shared/types/models/organisation';
import { PUBLIC_SUPPLIER_QUERY, INVITE_QUERY } from './queryKeys';

type PublicSupplierResponse = {
  id: string;
  name: string;
  logoUrl: string;
  companyNumber: string | null;
  country: string | null;
  sector: string | null;
  tradingNames: string[];
  website: string;
  pendingConnectionCount?: number;
  setupComplete: boolean;
};

export function usePublicSupplier(id: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [PUBLIC_SUPPLIER_QUERY, toRef(id)] as const,
      async queryFn({ queryKey }) {
        const res = await request<PublicSupplierResponse>(
          'GET',
          `/public/suppliers/${queryKey[1]}`,
        );
        return res.data;
      },
    }),
  );

  return { ...query, query, supplier: query.data };
}

type InviteResponse = {
  inviteID: string;
  senderOrgName: string;
  sendOrgID: string;
  sendOrgLogoURL: string;
  accepted: boolean;
  expired: boolean;
  cancelled: boolean;
  unclaimedOrgID: string;
};

function useInvite(id: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [INVITE_QUERY, toRef(id)] as const,
      async queryFn({ queryKey }) {
        const res = await request<InviteResponse>('GET', `/invites/${queryKey[1]}`);
        return res.data;
      },
    }),
  );

  return { ...query, query, invite: query.data };
}

export function useSignupSupplier() {
  // This localstorage item is populated by the catchSignupSource router guard
  const state = useLocalStorage('signupSource', undefined as string | undefined);

  const source = computed(() => {
    if (!state.value) return undefined;

    const parsed = JSON.parse(state.value);

    return typeof parsed === 'object' &&
      'id' in parsed &&
      typeof parsed.id === 'string' &&
      'type' in parsed &&
      typeof parsed.type === 'string'
      ? (parsed as { id: string; type: string })
      : undefined;
  });

  const {
    invite,
    query: inviteQuery,
    isLoading: isInviteLoading,
    isError: isInviteError,
  } = whenNonNull(
    () => source.value?.id,
    (enabled, id) => useInvite(id, { enabled }),
  );

  const supplierQuery = whenNonNull(
    () => invite.value?.unclaimedOrgID,
    (enabled, unclaimedOrgID) =>
      usePublicSupplier(unclaimedOrgID, {
        enabled: () => !invite.value?.cancelled && !invite.value?.expired && enabled(),
      }),
  );

  const isLoading = computed(() => isInviteLoading.value || supplierQuery.isLoading.value);
  const isError = computed(() => isInviteError.value || supplierQuery.isError.value);

  return {
    ...supplierQuery,
    queries: [inviteQuery, supplierQuery],
    isLoading,
    isError,
    invite,
    source,
  };
}

export function useClaimOrg(
  options?: MutationOptions<
    { org: Organisation; user: User },
    { org: Partial<Organisation>; user: Partial<User> }
  >,
) {
  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(variables) {
        const res = await request<{ org: Organisation; user: User }>('POST', '/claim-org', {
          org: variables.org,
          user: variables.user,
          httpReferrer: document.referrer.includes('localhost') ? undefined : document.referrer,
        });
        return res.data;
      },
    }),
  );
}

export function useCreateNewUser() {
  return useMutation<
    void,
    Error,
    { user: User; password: string; org: Organisation; inviteID: string | undefined },
    null
  >({
    async mutationFn({ user, password, org, inviteID }) {
      await cookieSignup({
        userId: user.userID,
        organisationId: org.orgID,
        email: user.email,
        password,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        invite: inviteID || '',
        marketing: user.marketingEmails,
      });
    },
  });
}

export function useConfirmSignup() {
  return useMutation<void, Error, { username: string; code: string }, null>({
    async mutationFn({ username, code }) {
      await cookieConfirmSignup(username, code);
    },
  });
}

export function useResendCode() {
  return useMutation<void, Error, { email: string }, null>({
    async mutationFn({ email }) {
      await cookieResendSignupCode(email);
    },
  });
}
